import {
  Users, Package, CalendarClock, Route, Receipt, MonitorSmartphone,
  GraduationCap, CheckSquare, FileText,
} from "lucide-react";
import ResourceManager from "../components/ResourceManager";
import { fmtCurrency, fmtDate } from "../lib/format";
import { mapsDirections } from "../lib/integrations";
import { MapPin } from "lucide-react";

const cur = (k) => (r) => <span className="font-medium text-navy-900">{fmtCurrency(r[k])}</span>;
const dat = (k) => (r) => <span className="text-slate-600">{fmtDate(r[k])}</span>;

const mileageNext = (row) => {
  const map = { Submitted: "Manager Approved", "Manager Approved": "Finance Approved", "Finance Approved": "Paid" };
  return map[row.approval_status] || "Paid";
};

export function Contacts() {
  return (
    <ResourceManager
      title="Contacts" subtitle="Decision makers, injectors and clinic stakeholders" icon={Users}
      endpoint="/contacts" searchKeys={["first_name", "last_name", "clinic_name", "email"]}
      columns={[
        { key: "first_name", label: "Name", primary: true, render: (r) => <span className="font-medium text-navy-900">{r.first_name} {r.last_name}</span> },
        { key: "position", label: "Position" },
        { key: "contact_role", label: "Role", badge: true },
        { key: "clinic_name", label: "Clinic" },
        { key: "email", label: "Email" },
        { key: "mobile", label: "Mobile" },
        { key: "relationship_status", label: "Relationship" },
      ]}
      fields={[
        { key: "first_name", label: "First Name", required: true },
        { key: "last_name", label: "Last Name", required: true },
        { key: "position", label: "Position" },
        { key: "contact_role", label: "Role", type: "select", options: ["Owner", "Manager", "Injector", "Trainer", "Decision Maker", "Influencer"] },
        { key: "clinic_name", label: "Clinic" },
        { key: "email", label: "Email" },
        { key: "mobile", label: "Mobile" },
        { key: "linkedin", label: "LinkedIn" },
        { key: "relationship_status", label: "Relationship", type: "select", options: ["Cold", "Warm", "Strong"] },
        { key: "birthday", label: "Birthday", type: "date" },
        { key: "decision_maker", label: "Decision Maker", type: "switch" },
        { key: "notes", label: "Notes", type: "textarea", full: true },
      ]}
    />
  );
}

export function Products() {
  return (
    <ResourceManager
      title="Products" subtitle="Injectables, devices and consumables catalogue" icon={Package}
      endpoint="/products" searchKeys={["name", "sku", "brand"]}
      columns={[
        { key: "name", label: "Product", primary: true },
        { key: "sku", label: "SKU" },
        { key: "category", label: "Category", badge: true },
        { key: "brand", label: "Brand" },
        { key: "list_price", label: "List Price", render: cur("list_price") },
        { key: "status", label: "Status", badge: true },
      ]}
      fields={[
        { key: "name", label: "Product Name", required: true },
        { key: "sku", label: "SKU" },
        { key: "category", label: "Category", type: "select", options: ["Injectable", "Device", "Skincare", "Consumables"] },
        { key: "brand", label: "Brand" },
        { key: "list_price", label: "List Price (NZD)", type: "number" },
        { key: "status", label: "Status", type: "select", options: ["Active", "Inactive"], default: "Active" },
      ]}
    />
  );
}

export function Meetings() {
  return (
    <ResourceManager
      title="Meetings" subtitle="Visits, calls, demos and training sessions" icon={CalendarClock}
      endpoint="/meetings" searchKeys={["clinic_name", "meeting_type", "bdm_name"]}
      columns={[
        { key: "date", label: "Date", render: dat("date") },
        { key: "meeting_type", label: "Type", badge: true },
        { key: "clinic_name", label: "Clinic", primary: true },
        { key: "bdm_name", label: "BDM" },
        { key: "location", label: "Location" },
        { key: "follow_up_date", label: "Follow-up", render: dat("follow_up_date") },
      ]}
      fields={[
        { key: "meeting_type", label: "Meeting Type", type: "select", required: true,
          options: ["Face-to-face", "Phone call", "Video call", "Product demo", "Training session", "Workshop", "Installation"] },
        { key: "clinic_name", label: "Clinic", required: true },
        { key: "date", label: "Date", type: "date" },
        { key: "time", label: "Time" },
        { key: "location", label: "Location" },
        { key: "attendees", label: "Attendees" },
        { key: "products_discussed", label: "Products Discussed" },
        { key: "competitors_mentioned", label: "Competitors Mentioned" },
        { key: "gps_checkin", label: "GPS Check-in (lat,lng)" },
        { key: "follow_up_date", label: "Follow-up Date", type: "date" },
        { key: "notes", label: "Meeting Notes", type: "textarea", full: true },
        { key: "next_actions", label: "Next Actions", type: "textarea", full: true },
      ]}
    />
  );
}

export function Mileage() {
  return (
    <ResourceManager
      title="Mileage Tracking" subtitle="NZ compliant trip log with approval workflow" icon={Route}
      endpoint="/mileage" searchKeys={["clinic_name", "destination", "bdm_name"]}
      approvals={{ module: "mileage", nextStatus: mileageNext }}
      columns={[
        { key: "date", label: "Date", render: dat("date") },
        { key: "bdm_name", label: "BDM", primary: true },
        { key: "start_location", label: "From" },
        { key: "destination", label: "To" },
        { key: "distance", label: "Distance", render: (r) => `${r.distance} km` },
        { key: "amount", label: "Amount", render: cur("amount") },
        { key: "approval_status", label: "Status", badge: true },
        { key: "map", label: "Route", render: (r) => (
          <a href={mapsDirections(r.start_location, r.destination)} target="_blank" rel="noreferrer"
            onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-brand hover:underline text-xs font-medium" data-testid={`map-link-${r.id}`}>
            <MapPin className="h-3.5 w-3.5" /> Map
          </a>
        ) },
      ]}
      fields={[
        { key: "date", label: "Date", type: "date", required: true },
        { key: "start_location", label: "Start Location", required: true },
        { key: "destination", label: "Destination", required: true },
        { key: "clinic_name", label: "Clinic Visited" },
        { key: "purpose", label: "Purpose" },
        { key: "start_odometer", label: "Start Odometer", type: "number" },
        { key: "end_odometer", label: "End Odometer", type: "number" },
        { key: "distance", label: "Distance (km)", type: "number" },
        { key: "rate", label: "Rate (per km)", type: "number", default: 1.04 },
        { key: "amount", label: "Amount (NZD)", type: "number" },
        { key: "approval_status", label: "Status", type: "select", options: ["Submitted", "Manager Approved", "Finance Approved", "Paid", "Rejected"], default: "Submitted" },
        { key: "notes", label: "Notes", type: "textarea", full: true },
      ]}
    />
  );
}

export function Expenses() {
  return (
    <ResourceManager
      title="Expense Management" subtitle="Capture, submit and approve business expenses" icon={Receipt}
      endpoint="/expenses" searchKeys={["category", "vendor", "bdm_name"]}
      approvals={{ module: "expenses", nextStatus: mileageNext }}
      columns={[
        { key: "date", label: "Date", render: dat("date") },
        { key: "category", label: "Category", badge: true },
        { key: "vendor", label: "Vendor", primary: true },
        { key: "bdm_name", label: "BDM" },
        { key: "amount", label: "Amount", render: cur("amount") },
        { key: "gst", label: "GST", render: cur("gst") },
        { key: "approval_status", label: "Status", badge: true },
      ]}
      fields={[
        { key: "date", label: "Date", type: "date", required: true },
        { key: "category", label: "Category", type: "select", required: true,
          options: ["Fuel", "Parking", "Flights", "Hotels", "Meals", "Entertainment", "Conference", "Training", "Courier", "Office Supplies", "Other"] },
        { key: "vendor", label: "Vendor" },
        { key: "amount", label: "Amount (NZD)", type: "number", required: true },
        { key: "gst", label: "GST", type: "number" },
        { key: "business_purpose", label: "Business Purpose" },
        { key: "client_related", label: "Client Related", type: "switch" },
        { key: "approval_status", label: "Status", type: "select", options: ["Submitted", "Manager Approved", "Finance Approved", "Paid", "Rejected"], default: "Submitted" },
      ]}
    />
  );
}

export function Devices() {
  return (
    <ResourceManager
      title="Device Installations" subtitle="MCT, Endolift and Icoone install register" icon={MonitorSmartphone}
      endpoint="/devices" searchKeys={["device_type", "serial_number", "clinic_name"]}
      columns={[
        { key: "device_type", label: "Device", badge: true },
        { key: "serial_number", label: "Serial No.", primary: true },
        { key: "clinic_name", label: "Clinic" },
        { key: "install_date", label: "Installed", render: dat("install_date") },
        { key: "trainer_name", label: "Trainer" },
        { key: "warranty_expiry", label: "Warranty Expiry", render: dat("warranty_expiry") },
      ]}
      fields={[
        { key: "device_type", label: "Device Type", type: "select", required: true, options: ["MCT", "Endolift", "Icoone", "Exo-Tech"] },
        { key: "serial_number", label: "Serial Number", required: true },
        { key: "clinic_name", label: "Clinic" },
        { key: "install_date", label: "Install Date", type: "date" },
        { key: "trainer_name", label: "Trainer" },
        { key: "warranty_expiry", label: "Warranty Expiry", type: "date" },
        { key: "service_reminder", label: "Service Reminder", type: "date" },
        { key: "notes", label: "Notes", type: "textarea", full: true },
      ]}
    />
  );
}

export function Workshops() {
  return (
    <ResourceManager
      title="Workshops & Training" subtitle="Events, webinars and certification programmes" icon={GraduationCap}
      endpoint="/workshops" searchKeys={["event_name", "trainer_name", "location"]}
      columns={[
        { key: "event_name", label: "Event", primary: true },
        { key: "event_type", label: "Type", badge: true },
        { key: "date", label: "Date", render: dat("date") },
        { key: "location", label: "Location" },
        { key: "trainer_name", label: "Trainer" },
        { key: "attendees", label: "Attendees" },
        { key: "certificates_issued", label: "Certificates" },
        { key: "feedback_score", label: "Score", render: (r) => `${r.feedback_score || "—"} / 5` },
      ]}
      fields={[
        { key: "event_name", label: "Event Name", required: true },
        { key: "event_type", label: "Type", type: "select", options: ["Workshop", "Training Event", "Webinar", "Certification"], default: "Workshop" },
        { key: "date", label: "Date", type: "date" },
        { key: "location", label: "Location" },
        { key: "trainer_name", label: "Trainer" },
        { key: "attendees", label: "Attendees", type: "number" },
        { key: "products_covered", label: "Products Covered" },
        { key: "certificates_issued", label: "Certificates Issued", type: "number" },
        { key: "feedback_score", label: "Feedback Score (/5)", type: "number" },
      ]}
    />
  );
}

export function Tasks() {
  return (
    <ResourceManager
      title="Tasks" subtitle="Follow-ups, reminders and action items" icon={CheckSquare}
      endpoint="/tasks" searchKeys={["title", "assigned_to_name", "clinic_name"]}
      columns={[
        { key: "title", label: "Task", primary: true },
        { key: "assigned_to_name", label: "Assigned To" },
        { key: "clinic_name", label: "Clinic" },
        { key: "due_date", label: "Due", render: dat("due_date") },
        { key: "priority", label: "Priority", badge: true },
        { key: "status", label: "Status", badge: true },
      ]}
      fields={[
        { key: "title", label: "Task Title", required: true, full: true },
        { key: "assigned_to_name", label: "Assigned To" },
        { key: "clinic_name", label: "Related Clinic" },
        { key: "due_date", label: "Due Date", type: "date" },
        { key: "priority", label: "Priority", type: "select", options: ["High", "Medium", "Low"], default: "Medium" },
        { key: "status", label: "Status", type: "select", options: ["To Do", "In Progress", "Done"], default: "To Do" },
        { key: "description", label: "Description", type: "textarea", full: true },
      ]}
    />
  );
}

export function Documents() {
  return (
    <ResourceManager
      title="Documents" subtitle="Contracts, price lists, certificates and assets" icon={FileText}
      endpoint="/documents" searchKeys={["name", "category"]}
      columns={[
        { key: "name", label: "Document", primary: true },
        { key: "category", label: "Category", badge: true },
        { key: "file_type", label: "Type" },
        { key: "size", label: "Size" },
        { key: "uploaded_by_name", label: "Uploaded By" },
        { key: "created_at", label: "Date", render: dat("created_at") },
      ]}
      fields={[
        { key: "name", label: "Document Name", required: true },
        { key: "category", label: "Category", type: "select", options: ["Contracts", "Price Lists", "Certificates", "Workshop Material", "Marketing Assets", "Training Records"] },
        { key: "file_type", label: "File Type", type: "select", options: ["pdf", "docx", "xlsx", "png", "jpg", "mp4", "pptx"] },
        { key: "size", label: "Size" },
        { key: "url", label: "File URL", full: true },
      ]}
    />
  );
}
